<template>
  <v-container class="pt-0" fluid>
    <v-row class="align-center">
      <v-col v-if="!typeSlug" cols="auto">
        <v-card-title class="pt-0 pl-0">
          Categories
        </v-card-title>
      </v-col>
      <v-spacer />
      <v-col cols="auto" style="min-width: 220px;">
        <select-project
          v-model="searchArgs.entity_id"
          label="Select project"
          @input="onProjectInput"
        />
      </v-col>
      <v-col v-if="!typeSlug" cols="auto" style="min-width: 180px;">
        <v-select
          v-model="searchArgs.type_id"
          :items="types"
          item-text="name"
          item-value="type_id"
          label="Type"
          clearable
          hide-details
          :disabled="!searchArgs.entity_id"
          @change="loadData"
        />
      </v-col>
      <v-col cols="auto">
        <v-btn
          color="primary"
          :loading="isLoading"
          :disabled="!searchArgs.entity_id"
          @click="onAdd"
        >
          Add Category
        </v-btn>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" class="pt-0">
        <v-card>
          <v-data-table
            :headers="headers"
            :items="items"
            :loading="isLoading"
            item-key="category_id"
            :sort-by="['sort_order']"
            fixed-header
            :height="tableHeight"
          >
            <template #item.name="{ item }">
              {{ item.name }}
              <v-chip v-if="!item.entity_id" x-small color="blue" outlined class="ml-1">default</v-chip>
            </template>
            <template #item.is_active="{ value }">
              <v-chip :color="value ? 'primary' : 'grey'" small outlined>
                {{ value ? 'Active' : 'Inactive' }}
              </v-chip>
            </template>
            <template #item.actions="{ item }">
              <template v-if="canModify(item)">
                <v-icon class="mr-2" @click="onEdit(item)">
                  mdi-pencil
                </v-icon>
                <v-icon color="error" @click="onDelete(item)">
                  mdi-delete
                </v-icon>
              </template>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <drawer-dialog
      v-model="isEditing"
      width="50%"
      max-width="800px"
    >
      <cms-category-form
        v-if="isEditing"
        :item="editingTarget"
        :types="types"
        :is-loading="isLoading"
        :entity-id="searchArgs.entity_id"
        :has-base="hasBase"
        @close="isEditing = false"
        @save="onSave"
      />
    </drawer-dialog>
  </v-container>
</template>

<script>
import { API } from '@/utils/api'
import { DrawerDialog } from '@/components/core/DrawerDialog'
import SelectProject from '@/components/core/SelectProject/SelectProject'
import CmsCategoryForm from './CmsCategoryForm'

export default {
  components: {
    DrawerDialog,
    SelectProject,
    CmsCategoryForm,
  },
  props: {
    typeSlug: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      isLoading: false,
      items: [],
      types: [],
      editingTarget: null,
      searchArgs: {
        entity_id: null,
        type_id: null,
      },
      headers: [
        { text: 'ID', value: 'category_id', width: 80 },
        { text: 'Slug', value: 'slug' },
        { text: 'Name', value: 'name' },
        { text: 'Type', value: 'type_name', width: 140 },
        { text: 'Sort Order', value: 'sort_order', width: 110 },
        { text: 'Status', value: 'is_active', width: 110 },
        { text: 'Created', value: 'created' },
        { text: 'Actions', value: 'actions', sortable: false, width: 120 },
      ],
    }
  },
  computed: {
    tableHeight() {
      return 'calc(100vh - 251px)'
    },
    hasBase() {
      const u = this.$store.state.auth.user
      return !!(u && u.settings && u.settings.base)
    },
    isEditing: {
      get() {
        return !!this.editingTarget
      },
      set(value) {
        if (!value) {
          this.editingTarget = null
        }
      },
    },
  },
  watch: {
    typeSlug() {
      if (this.searchArgs.entity_id) {
        this.applyTypeSlug()
        this.loadData()
      }
    },
  },
  methods: {
    canModify(item) {
      return !this.hasBase || !!item.entity_id
    },
    onProjectInput(val) {
      if (val) {
        this.loadTypes().then(() => {
          this.applyTypeSlug()
          this.loadData()
        })
      }
    },
    applyTypeSlug() {
      if (!this.typeSlug) {
        return
      }
      const type = this.types.find(t => t.slug === this.typeSlug)
      if (type) {
        this.searchArgs.type_id = type.type_id
      }
    },
    loadTypes() {
      return API.call('/cms/type/list.json', {}).then(r => {
        this.types = r.data.items || []
      })
    },
    loadData() {
      if (!this.searchArgs.entity_id) {
        return
      }
      this.isLoading = true
      return API.call('/cms/category/list.json', {
        entity_id: this.searchArgs.entity_id,
        type_id: this.searchArgs.type_id,
      })
        .then(r => {
          this.items = r.data.items || []
        })
        .finally(() => this.isLoading = false)
    },
    onAdd() {
      this.editingTarget = {
        entity_id: this.searchArgs.entity_id,
        type_id: this.searchArgs.type_id || null,
        slug: '',
        name: '',
        sort_order: 0,
        is_active: true,
      }
    },
    onEdit(item) {
      this.editingTarget = { ...item }
    },
    onSave(item) {
      this.isLoading = true
      return API.call('/cms/category/upsert.json', { ...item, entity_id: item.entity_id || 0 })
        .then(() => {
          this.$showSuccess('Category saved')
          this.editingTarget = null
          this.loadData()
        })
        .finally(() => this.isLoading = false)
    },
    onDelete(item) {
      this.$root.$confirm('Confirm', `Delete category "${item.name}"?`)
        .then(yes => {
          if (!yes) {
            return
          }
          this.isLoading = true
          API.call('/cms/category/delete.json', { category_id: item.category_id, entity_id: item.entity_id || 0 })
            .then(() => {
              this.$showSuccess('Category deleted')
              this.loadData()
            })
            .finally(() => this.isLoading = false)
        })
    },
  },
}
</script>
